import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Layout from '../components/Layout';
import { contactService } from '../services/contactService';
import { Contact, ContactStatus } from '../types/contact';

const ContactDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<ContactStatus>(ContactStatus.PENDING);

  useEffect(() => {
    const fetchContact = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const contactData = await contactService.getContactById(id);
        setContact(contactData);
        setNotes(contactData.notes || '');
        setStatus(contactData.status);
      } catch (err) {
        setError('Failed to load contact');
        console.error('Error fetching contact:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContact();
  }, [id]);

  const handleStatusChange = async (newStatus: ContactStatus) => {
    if (!id) return;

    try {
      await contactService.updateStatus(id, newStatus);
      setStatus(newStatus);
      if (contact) {
        setContact({ ...contact, status: newStatus });
      }
    } catch (err) {
      setError('Failed to update status');
      console.error('Error updating status:', err);
    }
  };

  const handleNotesChange = async (newNotes: string) => {
    if (!id) return;

    try {
      await contactService.updateContact(id, { notes: newNotes });
      setNotes(newNotes);
      if (contact) {
        setContact({ ...contact, notes: newNotes });
      }
    } catch (err) {
      setError('Failed to update notes');
      console.error('Error updating notes:', err);
    }
  };

  const handleMarkAsRead = async () => {
    if (!id || !contact) return;

    try {
      await contactService.markAsRead(id);
      setContact({ ...contact, isRead: true });
    } catch (err) {
      setError('Failed to mark as read');
      console.error('Error marking as read:', err);
    }
  };

  const getStatusBadge = (status: ContactStatus) => {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full';
    switch (status) {
      case ContactStatus.PENDING:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case ContactStatus.IN_PROGRESS:
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case ContactStatus.RESOLVED:
        return `${baseClasses} bg-green-100 text-green-800`;
      case ContactStatus.CLOSED:
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !contact) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error || 'Contact not found'}
          </div>
          <button
            onClick={() => navigate('/contacts')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Back to Contacts
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <button
            onClick={() => navigate('/contacts')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="-ml-1 mr-1 h-4 w-4" />
            Back to Contacts
          </button>
          <div className="flex items-center space-x-4">
            <span className={getStatusBadge(contact.status)}>
              {contact.status}
            </span>
            {!contact.isRead && (
              <button
                onClick={handleMarkAsRead}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Mark as Read
              </button>
            )}
          </div>
        </div>

        {/* Contact Details */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Contact Information
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Received on {formatDate(contact.createdAt)}
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{contact.name}</dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="mt-1 text-sm text-gray-900">{contact.email}</dd>
              </div>
              {contact.phone && (
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1 text-sm text-gray-900">{contact.phone}</dd>
                </div>
              )}
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">IP Address</dt>
                <dd className="mt-1 text-sm text-gray-900">{contact.ip}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Subject</dt>
                <dd className="mt-1 text-sm text-gray-900">{contact.subject}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Message</dt>
                <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                  {contact.message}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Status and Notes */}
        <div className="mt-8 space-y-6">
          {/* Status Update */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Update Status
              </h3>
              <div className="mt-4">
                <select
                  value={status}
                  onChange={(e) => handleStatusChange(e.target.value as ContactStatus)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value={ContactStatus.PENDING}>Pending</option>
                  <option value={ContactStatus.IN_PROGRESS}>In Progress</option>
                  <option value={ContactStatus.RESOLVED}>Resolved</option>
                  <option value={ContactStatus.CLOSED}>Closed</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Notes
              </h3>
              <div className="mt-4">
                <textarea
                  value={notes}
                  onChange={(e) => handleNotesChange(e.target.value)}
                  rows={4}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="Add notes about this contact..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ContactDetail; 